/**
 * Translated Zod Validation Utilities
 *
 * This module provides utilities for creating Zod schemas with i18n-compatible
 * validation messages. Instead of hardcoding error messages, schemas use
 * translation keys that are resolved at render time.
 *
 * ## Pattern 1: Translation Keys in Schema (Recommended)
 *
 * Use translation keys as error messages in your schema:
 * ```typescript
 * const schema = z.object({
 *   email: z.string().min(1, 'emailRequired').email('email'),
 *   password: z.string().min(8, 'passwordMinLength'),
 * });
 * ```
 *
 * Then resolve them in your component:
 * ```tsx
 * {fieldState.error && (
 *   <p className="text-destructive">
 *     {t(`validation.${fieldState.error.message}`, { count: 8 })}
 *   </p>
 * )}
 * ```
 *
 * ## Pattern 2: Schema Factory with t() (For complex schemas)
 *
 * ```typescript
 * const createSchema = (t: TFunction) => z.object({
 *   email: z.string().min(1, t('validation.emailRequired')),
 * });
 *
 * // In component:
 * const { t } = useTranslation('common');
 * const schema = useMemo(() => createSchema(t), [t]);
 * ```
 *
 * ## Pattern 3: Using the resolveValidationMessage helper
 *
 * ```tsx
 * import { resolveValidationMessage } from '@/lib/i18n/validation';
 *
 * {fieldState.error && (
 *   <p className="text-destructive">
 *     {resolveValidationMessage(t, fieldState.error.message)}
 *   </p>
 * )}
 * ```
 */

import type { TFunction } from 'i18next';

/**
 * Common validation translation keys.
 * Use these as error messages in Zod schemas.
 */
export const ValidationKeys = {
  // Generic
  required: 'required',
  email: 'email',
  minLength: 'minLength',
  maxLength: 'maxLength',

  // Field-specific
  fullNameRequired: 'fullNameRequired',
  emailRequired: 'emailRequired',
  passwordRequired: 'passwordRequired',
  passwordMinLength: 'passwordMinLength',
  passwordsDoNotMatch: 'passwordsDoNotMatch',
  confirmPasswordRequired: 'confirmPasswordRequired',
  currentPasswordRequired: 'currentPasswordRequired',
  passwordForVerification: 'passwordForVerification',

  // Business entities
  companyNameRequired: 'companyNameRequired',
  companyNameMinLength: 'companyNameMinLength',
  companyNameMaxLength: 'companyNameMaxLength',
  phoneRequired: 'phoneRequired',
  phoneMaxLength: 'phoneMaxLength',
  vehicleRequired: 'vehicleRequired',
  customerRequired: 'customerRequired',
  supplierRequired: 'supplierRequired',
  titleRequired: 'titleRequired',

  // Date/time
  endAfterStart: 'endAfterStart',
  endDateAfterStart: 'endDateAfterStart',
} as const;

export type ValidationKey = (typeof ValidationKeys)[keyof typeof ValidationKeys];

/**
 * Resolves a validation message using the translation function.
 * If the message is a known validation key, it will be translated.
 * Otherwise, the original message is returned.
 *
 * @param t - The translation function from useTranslation
 * @param message - The error message (either a translation key or literal string)
 * @param params - Optional interpolation parameters (e.g., { count: 8 })
 * @returns The translated message
 *
 * @example
 * ```tsx
 * const { t } = useTranslation('common');
 *
 * {fieldState.error && (
 *   <p className="text-destructive">
 *     {resolveValidationMessage(t, fieldState.error.message, { count: 8 })}
 *   </p>
 * )}
 * ```
 */
export function resolveValidationMessage(
  t: TFunction,
  message: string | undefined,
  params?: Record<string, unknown>
): string {
  if (!message) return '';

  // Check if it's a known validation key
  const isValidationKey = Object.values(ValidationKeys).includes(message as ValidationKey);

  if (isValidationKey) {
    return t(`validation.${message}`, params);
  }

  // Check if it looks like a translation key (no spaces, camelCase or dot notation)
  const looksLikeKey = /^[a-zA-Z][a-zA-Z0-9.]*$/.test(message) && !message.includes(' ');

  if (looksLikeKey) {
    // Try to translate it, fall back to original if not found
    const translated = t(`validation.${message}`, { defaultValue: message, ...params });
    return translated;
  }

  // Return as-is if it's a literal string
  return message;
}

/**
 * Example usage in a component with FormMessage:
 *
 * ```tsx
 * import { resolveValidationMessage } from '@/lib/i18n/validation';
 *
 * function MyForm() {
 *   const { t } = useTranslation('common');
 *
 *   return (
 *     <FormField
 *       render={({ field, fieldState }) => (
 *         <FormItem>
 *           <FormControl>
 *             <Input {...field} />
 *           </FormControl>
 *           {fieldState.error && (
 *             <p className="text-xs text-destructive" role="alert">
 *               {resolveValidationMessage(t, fieldState.error.message, { count: 8 })}
 *             </p>
 *           )}
 *         </FormItem>
 *       )}
 *     />
 *   );
 * }
 * ```
 */
